import { Controller } from "@hotwired/stimulus"

// Learner clinical decision pathway block (TMS-773 / TMS-953): one step shows
// at a time with "Step X of Y" progress, Previous/Next navigation (disabled
// at the ends), a per-step illustration (falling back to the pathway default),
// and per-option recommendations on decision steps. Choices are client-side
// only — nothing is persisted — and stay re-choosable when the student
// navigates back. Illustrations are pre-rendered and shown/hidden with the
// step so moving between steps never swaps a src (no flash of the previous
// image or a broken state).
export default class extends Controller {
  static targets = [
    "step",
    "illustration",
    "illustrationFrame",
    "counter",
    "progressFill",
    "previousButton",
    "nextButton",
    "option",
    "recommendation"
  ]

  connect() {
    this.showStep(0)
  }

  previous(event) {
    event.preventDefault()
    this.showStep(this.currentIndex - 1)
  }

  next(event) {
    event.preventDefault()
    this.showStep(this.currentIndex + 1)
  }

  chooseOption(event) {
    event.preventDefault()

    const stepIndex = Number(event.currentTarget.dataset.stepIndex)
    const optionIndex = Number(event.currentTarget.dataset.optionIndex)

    this.optionTargets.forEach((option) => {
      if (Number(option.dataset.stepIndex) !== stepIndex) return

      const selected = Number(option.dataset.optionIndex) === optionIndex
      option.classList.toggle("is-selected", selected)
      option.setAttribute("aria-pressed", String(selected))
    })

    this.recommendationTargets.forEach((recommendation) => {
      if (Number(recommendation.dataset.stepIndex) !== stepIndex) return

      recommendation.hidden = Number(recommendation.dataset.optionIndex) !== optionIndex
    })
  }

  showStep(index) {
    const total = this.stepTargets.length
    const nextIndex = Math.min(Math.max(index, 0), total - 1)

    this.stepTargets.forEach((step) => {
      step.hidden = Number(step.dataset.stepIndex) !== nextIndex
    })

    this.showIllustration(nextIndex)

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `Step ${nextIndex + 1} of ${total}`
    }
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${((nextIndex + 1) / total) * 100}%`
    }
    if (this.hasPreviousButtonTarget) {
      this.previousButtonTarget.disabled = nextIndex === 0
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = nextIndex === total - 1
    }
  }

  showIllustration(index) {
    if (!this.hasIllustrationTarget) return

    this.illustrationTargets.forEach((image) => {
      image.hidden = Number(image.dataset.stepIndex) !== index
    })

    if (this.hasIllustrationFrameTarget) {
      this.illustrationFrameTarget.hidden = !this.illustrationTargets.some((image) => !image.hidden)
    }
  }

  get currentIndex() {
    const step = this.stepTargets.find((candidate) => !candidate.hidden)

    return step ? Number(step.dataset.stepIndex) : 0
  }
}
